import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import {
  ComplianceCheck,
  ComplianceItem,
  ComplianceItemCompletion,
  ComplianceReport,
  CreateComplianceItemPayload,
  CustomerUpdateComplianceItemPayload,
  UpdateComplianceItemPayload
} from 'src/models/compliance.model';

@Injectable({ providedIn: 'root' })
export class ComplianceApiService {
  private readonly http = inject(HttpClient);
  private readonly customerUrl = `${environment.api_url}api/v1/customer/compliance-items`;

  // --- Customer endpoints ---

  getItems(addressId: number, filters?: { bucket?: string; status?: string }): Observable<ComplianceItem[]> {
    let params = new HttpParams().set('address_id', addressId.toString());
    if (filters?.bucket) {
      params = params.set('bucket', filters.bucket);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    return this.http.get<ComplianceItem[]>(this.customerUrl, { params });
  }

  getItem(id: number): Observable<ComplianceItem> {
    return this.http.get<ComplianceItem>(`${this.customerUrl}/${id}`);
  }

  getCheckStatus(addressId: number): Observable<ComplianceCheck> {
    const params = new HttpParams().set('address_id', addressId.toString());
    return this.http.get<ComplianceCheck>(`${this.customerUrl}/check-status`, { params });
  }

  triggerCheck(addressId: number): Observable<ComplianceCheck> {
    return this.http.post<ComplianceCheck>(`${this.customerUrl}/trigger-check`, {
      address_id: addressId,
    });
  }

  getReport(addressId: number): Observable<ComplianceReport | null> {
    const params = new HttpParams().set('address_id', addressId.toString());
    return this.http.get<ComplianceReport | null>(`${this.customerUrl}/report`, { params });
  }

  customerUpdateItem(id: number, payload: CustomerUpdateComplianceItemPayload): Observable<ComplianceItem> {
    return this.http.put<ComplianceItem>(`${this.customerUrl}/${id}`, payload);
  }

  getCompletions(complianceItemId: number): Observable<ComplianceItemCompletion[]> {
    return this.http.get<ComplianceItemCompletion[]>(`${this.customerUrl}/${complianceItemId}/completions`);
  }

  recordCompletion(complianceItemId: number, payload: { completed_at?: string; notes?: string }): Observable<ComplianceItemCompletion> {
    return this.http.post<ComplianceItemCompletion>(`${this.customerUrl}/${complianceItemId}/completions`, payload);
  }

  createItem(payload: CreateComplianceItemPayload): Observable<ComplianceItem> {
    return this.http.post<ComplianceItem>(this.customerUrl, payload);
  }

  updateItem(id: number, payload: UpdateComplianceItemPayload): Observable<ComplianceItem> {
    return this.http.put<ComplianceItem>(`${this.customerUrl}/${id}`, payload);
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.customerUrl}/${id}`);
  }

  deleteCompletion(complianceItemId: number, completionId: number): Observable<void> {
    return this.http.delete<void>(`${this.customerUrl}/${complianceItemId}/completions/${completionId}`);
  }
}
